import React, { useCallback, useEffect, useState } from 'react';
import { adminAPI } from '../services/api';
import { formatVsPar } from '../utils/handicap';
import './AdminPartidas.css';

const ESTADOS = {
  esperando: 'Sin empezar',
  en_curso: 'En curso',
  finalizada: 'Finalizada'
};

// Registro de partidas jugadas, dentro del panel de administración.
function AdminPartidas({ onError }) {
  const [resumen, setResumen] = useState(null);
  const [partidas, setPartidas] = useState([]);
  const [total, setTotal] = useState(0);
  const [detalle, setDetalle] = useState(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const [resumenResp, partidasResp] = await Promise.all([
        adminAPI.resumen(),
        adminAPI.partidas(50, 0)
      ]);
      setResumen(resumenResp.data);
      setPartidas(partidasResp.data.partidas);
      setTotal(partidasResp.data.total);
    } catch (err) {
      onError(err, 'No se pudieron cargar las partidas');
    } finally {
      setCargando(false);
    }
  }, [onError]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const verDetalle = async (codigo) => {
    try {
      const respuesta = await adminAPI.partida(codigo);
      setDetalle(respuesta.data);
    } catch (err) {
      onError(err, 'No se pudo cargar la partida');
    }
  };

  const borrar = async (codigo) => {
    if (!window.confirm(`¿Borrar la partida ${codigo}? Se perderán sus golpes y resultados.`)) {
      return;
    }
    try {
      await adminAPI.borrarPartida(codigo);
      setDetalle(null);
      await cargar();
    } catch (err) {
      onError(err, 'No se pudo borrar la partida');
    }
  };

  const fechaHora = (valor) => {
    if (!valor) return '—';
    return new Date(valor).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (cargando) {
    return <div className="card"><p>Cargando partidas...</p></div>;
  }

  return (
    <>
      {resumen && (
        <div className="card">
          <div className="resumen-grid">
            <div className="resumen-dato">
              <div className="resumen-valor">{resumen.partidas}</div>
              <div className="resumen-etiqueta">Partidas</div>
            </div>
            <div className="resumen-dato">
              <div className="resumen-valor">{resumen.finalizadas}</div>
              <div className="resumen-etiqueta">Finalizadas</div>
            </div>
            <div className="resumen-dato">
              <div className="resumen-valor">{resumen.en_curso}</div>
              <div className="resumen-etiqueta">En curso</div>
            </div>
            <div className="resumen-dato">
              <div className="resumen-valor">{resumen.usuarios}</div>
              <div className="resumen-etiqueta">Registrados</div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <h2 className="bloque-titulo">
          Partidas {total > partidas.length && <span className="bloque-nota">({partidas.length} de {total})</span>}
        </h2>

        {partidas.length === 0 ? (
          <p className="sin-datos">Todavía no se ha jugado ninguna partida.</p>
        ) : (
          <div className="registro-lista">
            {partidas.map(partida => (
              <div key={partida.id} className="registro-item">
                <div className="registro-principal">
                  <span className="registro-codigo">{partida.codigo}</span>
                  <span className={`registro-estado ${partida.estado}`}>
                    {ESTADOS[partida.estado] || partida.estado}
                  </span>
                </div>
                <div className="registro-datos">
                  {fechaHora(partida.fecha_inicio || partida.created_at)} ·
                  {' '}{partida.num_jugadores} jugadores
                  {partida.num_registrados > 0 && ` (${partida.num_registrados} con cuenta)`} ·
                  {' '}{partida.golpes_anotados} golpes
                </div>
                <button className="registro-ver" onClick={() => verDetalle(partida.codigo)}>
                  Ver detalle
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {detalle && (
        <div className="modal-overlay" onClick={() => setDetalle(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-cabecera">
              <h2>Partida {detalle.codigo}</h2>
              <button className="modal-cerrar" onClick={() => setDetalle(null)}>✕</button>
            </div>

            <p className="modal-meta">
              {detalle.campo_nombre} · {ESTADOS[detalle.estado] || detalle.estado}<br />
              Creada: {fechaHora(detalle.created_at)}<br />
              {detalle.fecha_inicio && <>Inicio: {fechaHora(detalle.fecha_inicio)}<br /></>}
              {detalle.fecha_fin && <>Fin: {fechaHora(detalle.fecha_fin)}</>}
            </p>

            <div className="tabla-scroll">
              <table className="tabla-jugadores">
                <thead>
                  <tr>
                    <th>Jugador</th>
                    <th>Hcp</th>
                    <th>Golpes</th>
                    <th>Su par</th>
                    <th>±</th>
                    <th>Pts</th>
                    <th>Pos</th>
                  </tr>
                </thead>
                <tbody>
                  {detalle.jugadores.map(jugador => (
                    <tr key={jugador.id}>
                      <td>
                        {jugador.nombre}
                        {jugador.usuario_email && (
                          <span className="jugador-cuenta" title={jugador.usuario_email}>cuenta</span>
                        )}
                      </td>
                      <td>{jugador.handicap}</td>
                      <td>{jugador.total_golpes ?? jugador.golpes_en_curso}</td>
                      <td>{jugador.par_personal ?? '—'}</td>
                      <td>{jugador.vs_par === null || jugador.vs_par === undefined ? '—' : formatVsPar(jugador.vs_par)}</td>
                      <td>{jugador.puntos_stableford ?? '—'}</td>
                      <td>{jugador.posicion ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              className="btn btn-danger btn-full"
              onClick={() => borrar(detalle.codigo)}
              style={{ marginTop: '16px' }}
            >
              Borrar esta partida
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminPartidas;
